/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;

    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    this.abortController = null;
    this.requestIndex = 0;
    this.searchFields = 'title,product_type,vendor,variants.title,variants.sku';

    if (!this.form || !this.input) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.button.forEach((item, i) => {
      item.addEventListener('click', (event) => {
        var _this = this;
        event.preventDefault();
        document.getElementsByTagName("body")[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
        if (this.container.classList.contains('active')) {
          setTimeout(function () {
            _this.input.focus({
              preventScroll: true
            });
          }, 100);
          dispatchCustomEvent('search:open');
        }

        return false;
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!this.predictiveSearchResults) {
      return;
    }

    if (!searchTerm.length) {
      this.abortController?.abort();
      this.predictiveSearchResults.classList.remove('loading');
      this.predictiveSearchResults.classList.remove('active');
      this.predictiveSearchResults.innerHTML = '';
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (!this.predictiveSearchResults) {
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestIndex = ++this.requestIndex;

    this.predictiveSearchResults.classList.add('loading');

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': this.searchFields,
      section_id: 'predictive-search'
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestIndex !== this.requestIndex) return;

        const parsedResponse = new DOMParser().parseFromString(text, 'text/html');
        const resultsContainer = parsedResponse.querySelector('#shopify-section-predictive-search');
        if (!resultsContainer) {
          return this.getSearchResultsFromJson(searchTerm, requestIndex);
        }
        const resultsMarkup = resultsContainer.innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        return this.getSearchResultsFromJson(searchTerm, requestIndex);
      })
      .finally(() => {
        if (requestIndex === this.requestIndex) {
          this.predictiveSearchResults.classList.remove('loading');
        }
      });
  }

  getSearchResultsFromJson(searchTerm, requestIndex) {
    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': this.searchFields
    });

    return fetch(`${theme.routes.predictive_search_url}.json?${params.toString()}`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.json();
      })
      .then((response) => {
        if (requestIndex !== this.requestIndex) return;

        const resultsMarkup = this.renderSearchResultsFromJson(searchTerm, response.resources?.results || {});
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  renderSearchResultsFromJson(searchTerm, results) {
    const queries = results.queries || [];
    const products = results.products || [];
    const articles = results.articles || [];
    const hasResults = queries.length || products.length || articles.length;

    return `
      <div class="thb-predictive-search">
        ${queries.length ? `
          <div class="thb-predictive-search--popular">
            <span class="thb-predictive-search--label">${this.escapeHTML(window.theme.strings?.suggestions || 'Suggestions')}</span>
            <scroll-shadow>
              <div>
                ${queries.slice(0, 8).map((query) => {
                  const queryText = typeof query === 'string' ? query : (query.text || '');
                  const queryLabel = typeof query === 'string' ? query : (query.styled_text || query.text || '');
                  return `<a href="${theme.routes.search_url}?q=${encodeURIComponent(queryText)}&type=product" title="${this.escapeHTML(queryText)}">${queryLabel}</a>`;
                }).join('')}
              </div>
            </scroll-shadow>
          </div>` : ''}
        <scroll-shadow>
          <div class="side-panel-content--inner">
            ${products.length ? `
              <div class="thb-predictive-search--group">
                <span class="thb-predictive-search--label">${this.escapeHTML(window.theme.strings?.searchProducts || 'Products')}</span>
                <div class="thb-predictive-search--results">
                  ${products.slice(0, 6).map((product) => this.renderProductResult(searchTerm, product)).join('')}
                </div>
              </div>` : ''}
            ${articles.length ? `
              <div class="thb-predictive-search--group">
                <span class="thb-predictive-search--label">${this.escapeHTML(window.theme.strings?.articles || 'Articles')}</span>
                <div class="thb-predictive-search--articles">
                  ${articles.slice(0, 4).map((article) => `
                    <a href="${article.url}" class="thb-predictive-search--article" title="${this.escapeHTML(article.title)}">
                      <span class="thb-predictive-search--article-title">${this.highlightText(article.title, searchTerm)}</span>
                    </a>
                  `).join('')}
                </div>
              </div>` : ''}
            ${!hasResults ? `<p class="thb-predictive-search--empty">${this.escapeHTML((window.theme.strings?.searchNoResults || 'No results found for "__TERMS__".').replace('__TERMS__', searchTerm))}</p>` : ''}
          </div>
        </scroll-shadow>
        <div class="side-panel-content--search-footer">
          <button id="search-results-submit" class="button full outline" type="button">
            <span>${this.escapeHTML(window.theme.strings?.viewAllResults || 'View all results')}</span>
          </button>
        </div>
      </div>
    `;
  }

  renderProductResult(searchTerm, product) {
    const image = product.featured_image?.url || product.featured_image || product.image || '';
    const price = typeof formatMoney === 'function'
      ? formatMoney(parseFloat(product.price || 0) * 100, theme.settings.money_with_currency_format || '${{amount}}')
      : product.price;

    return `
      <div class="thb-predictive-search--result">
        <product-card class="product-cart-item">
          <div class="product-cart-item-image">
            ${image ? `<img src="${image}" alt="${this.escapeHTML(product.title)}" loading="lazy">` : '<div class="thb-placeholder" style="height:75px;"></div>'}
          </div>
          <div>
            <a href="${product.url}" title="${this.escapeHTML(product.title)}" class="cart-product-link">
              ${this.highlightText(product.title, searchTerm)}
            </a>
            <span class="price">${price}</span>
          </div>
        </product-card>
      </div>
    `;
  }

  highlightText(text, searchTerm) {
    if (!text) return '';
    const escapedText = this.escapeHTML(text);
    if (!searchTerm) return escapedText;

    const terms = searchTerm
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    if (!terms.length) return escapedText;

    return escapedText.replace(new RegExp(`(${terms.join('|')})`, 'gi'), '<mark>$1</mark>');
  }

  escapeHTML(value) {
    return `${value || ''}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    let _this = this,
      submitButton = this.container.querySelector('#search-results-submit');

    if (submitButton) {
      submitButton.addEventListener('click', () => {
        _this.form.submit();
      });
    }
  }

  close() {
    this.container.classList.remove('active');
  }
}
window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch();
  }
});
